/**
 * QuickLogHabitsCard.jsx — iter933-Batch3-i (Feb 2026)
 *
 * One-tap daily habit tracker for the Living Metabolism engine. Each
 * habit sends a POST to /api/living-metabolism/log with the correct
 * pathways_impacted list — no free-text required from the patient.
 *
 * The card renders 3 columns:
 *   • ✅ Полезни: cruciferous, Ω-3, DIM, sulforaphane, fasting, movement…
 *   • 😴 Стил на живот: sleep, meditation, hydration
 *   • ⚠️ Внимание: sugar, alcohol, processed foods (logged as "skipped"
 *     against anti-inflammatory pathways → mild negative boost)
 *
 * After tap: pill turns emerald with ✓, disabled for 30s to prevent
 * spam. Emits `habits-changed` event so BHI panel refetches env boosts.
 *
 * Educational only. Not a medical device.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Check } from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { toast } from "sonner";
import { emitHealthEvent } from "@/utils/healthEvents";

// Habit registry — {id, emoji, label_bg, action_type, pathways}
const HABITS = [
  // ── Positive: nutrient / phytochemical intake ────────────
  { id: "cruciferous",       emoji: "🥦", label_bg: "Крестоцветни зеленчуци",  action: "executed",
    pathways: ["cruciferous_vegetables", "estrogen_metabolism", "liver_phase_2_conjugation"], group: "food" },
  { id: "dim_i3c",           emoji: "💊", label_bg: "DIM / I3C добавка",       action: "executed",
    pathways: ["dim_i3c_intake", "estrogen_metabolism"], group: "food" },
  { id: "sulforaphane",      emoji: "🌱", label_bg: "Сулфорафан",              action: "executed",
    pathways: ["nrf2_keap1_pathway", "polyphenol_antioxidant"], group: "food" },
  { id: "omega3",            emoji: "🐟", label_bg: "Ω-3 (риба/добавка)",     action: "executed",
    pathways: ["omega3_epa_dha", "polyphenol_antioxidant"], group: "food" },
  { id: "fiber_25g",         emoji: "🌾", label_bg: "Фибри ≥25g",              action: "executed",
    pathways: ["fiber_intake", "fiber_estrogen_binding", "gut_microbiome"], group: "food" },
  { id: "polyphenol",        emoji: "🫐", label_bg: "Полифеноли (боровинки/зелен чай)",   action: "executed",
    pathways: ["polyphenol_antioxidant"], group: "food" },
  // ── Positive: lifestyle ─────────────────────────────────
  { id: "fasting_14h",       emoji: "⏰", label_bg: "Фастинг ≥14ч",             action: "executed",
    pathways: ["intermittent_fasting", "amp_kinase_activation", "mtor_modulation"], group: "lifestyle" },
  { id: "sleep_7_9h",        emoji: "😴", label_bg: "Сън 7-9ч",                 action: "executed",
    pathways: ["sleep_7_9h", "meditation_stress"], group: "lifestyle" },
  { id: "movement_30min",    emoji: "🚶", label_bg: "≥30 мин движение",         action: "executed",
    pathways: ["cardio_moderate", "amp_kinase_activation"], group: "lifestyle" },
  { id: "resistance_train",  emoji: "🏋️", label_bg: "Тренировка със съпротива", action: "executed",
    pathways: ["resistance_training", "insulin_signaling"], group: "lifestyle" },
  { id: "meditation",        emoji: "🧘", label_bg: "Медитация / стрес мениджмънт", action: "executed",
    pathways: ["meditation_stress"], group: "lifestyle" },
  // ── Negative: logged as "skipped" against anti-inflammatory axes ─
  { id: "processed_food",    emoji: "🍔", label_bg: "Ултра-обработена храна",  action: "skipped",
    pathways: ["low_glycemic_diet", "polyphenol_antioxidant", "insulin_signaling"], group: "attention" },
  { id: "added_sugar",       emoji: "🍬", label_bg: "Добавена захар",           action: "skipped",
    pathways: ["low_glycemic_diet", "insulin_signaling"], group: "attention" },
  { id: "alcohol",           emoji: "🍷", label_bg: "Алкохол",                  action: "skipped",
    pathways: ["alcohol_moderation", "estrogen_metabolism"], group: "attention" },
];

const GROUP_META = {
  food:      { title_bg: "🥗 Хранене",         accent: "emerald" },
  lifestyle: { title_bg: "🌿 Стил на живот",    accent: "cyan" },
  attention: { title_bg: "⚠️ Днес се случиха",  accent: "rose" },
};

export default function QuickLogHabitsCard() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const bg = language === "bg";

  const [busy, setBusy] = useState(null);
  const [flashed, setFlashed] = useState({});
  const [todayLogged, setTodayLogged] = useState({});
  const [conditions, setConditions] = useState([]);         // iter933 — from health-conditions
  const [promptIdx, setPromptIdx] = useState(0);            // rotating prompt carousel

  // iter933 — Pull the patient's accepted conditions so we can prioritize
  // WHICH missing habits to remind about (e.g., breast history → DIM +
  // cruciferous; insulin resistance → fasting + fiber; inflammation → Ω-3).
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [today, hc] = await Promise.all([
          axios.get(`${API}/living-metabolism/today`).catch(() => ({ data: {} })),
          axios.get(`${API}/health-conditions`).catch(() => ({ data: [] })),
        ]);
        if (!mounted) return;
        const done = {};
        for (const e of (today?.data?.entries || [])) done[e.recommendation_id] = true;
        setTodayLogged(done);
        const conds = Array.isArray(hc.data) ? hc.data : (hc.data?.conditions || []);
        setConditions(conds.map((c) => (c.condition_id || "").toLowerCase()));
      } catch (err) { /* soft-fail */ }
    })();
    return () => { mounted = false; };
  }, [API]);

  const logHabit = useCallback(async (habit) => {
    if (busy || flashed[habit.id] || todayLogged[habit.id]) return;
    setBusy(habit.id);
    try {
      const { data } = await axios.post(`${API}/living-metabolism/log`, {
        recommendation_id: habit.id,
        action_type:       habit.action,
        pathways:          habit.pathways,
        source_module:     "quick_log",
        note:              habit.label_bg,
      });
      if (data?.duplicate) {
        toast.info(bg ? "Вече отбелязано днес." : "Already logged today.");
      } else {
        const msg = habit.action === "executed"
          ? (bg ? `+ ${habit.label_bg} — тласък към метаболизма` : `+ ${habit.label_bg} — pushed metabolism`)
          : (bg ? `${habit.label_bg} отбелязано — леко натоварване` : `${habit.label_bg} logged — mild burden`);
        toast.success(msg);
      }
      setTodayLogged((prev) => ({ ...prev, [habit.id]: true }));
      setFlashed((prev) => ({ ...prev, [habit.id]: true }));
      // Notify BHI/Dashboard to re-fetch env boosts
      emitHealthEvent("habits-changed", { habit_id: habit.id, action: habit.action });
    } catch (err) {
      toast.error(bg ? "Грешка при отбелязване. Опитайте пак." : "Log failed. Retry.");
    } finally {
      setBusy(null);
      setTimeout(() => {
        setFlashed((prev) => { const p = { ...prev }; delete p[habit.id]; return p; });
      }, 30000);
    }
  }, [API, bg, busy, flashed, todayLogged]);

  const groups = { food: [], lifestyle: [], attention: [] };
  for (const h of HABITS) groups[h.group].push(h);

  // iter933 — Derive "forgotten today" prompts. Priority order:
  //   1. Habits mapped to accepted health conditions (BDI/BPP targets).
  //   2. Core anti-inflammatory / metabolic habits (Ω-3, movement, sleep).
  // Only the POSITIVE habits (action="executed") are surfaced as prompts.
  const missedPrompts = React.useMemo(() => {
    if (!bg) return [];   // MVP: Bulgarian only for now
    const CONDITION_TO_HABITS = {
      // (accepted condition substring → priority habit ids)
      "insulin":          ["fiber_25g", "fasting_14h", "resistance_train"],
      "estrogen":         ["cruciferous", "dim_i3c", "fiber_25g"],
      "hcysteine":        ["omega3"],
      "dyslipid":         ["omega3", "movement_30min"],
      "hyperlipid":       ["omega3", "polyphenol"],
      "inflammation":     ["omega3", "polyphenol", "meditation"],
      "breast":           ["cruciferous", "dim_i3c", "sulforaphane"],
      "cyst":             ["cruciferous", "dim_i3c"],
      "adenoma":          ["cruciferous", "sulforaphane"],
      "nafld":            ["fiber_25g", "fasting_14h", "movement_30min"],
      "hypertension":     ["movement_30min", "polyphenol"],
      "anemia":           ["omega3"],
      "thyroid":          ["polyphenol", "sleep_7_9h"],
      "hypothyroid":      ["polyphenol", "sleep_7_9h"],
      "polycystic":       ["fasting_14h", "resistance_train", "cruciferous"],
      "pcos":             ["fasting_14h", "resistance_train"],
    };
    const CORE_UNIVERSAL = ["omega3", "movement_30min", "sleep_7_9h", "cruciferous"];
    const priority = new Set();
    for (const c of conditions) {
      for (const [needle, ids] of Object.entries(CONDITION_TO_HABITS)) {
        if (c.includes(needle)) ids.forEach((i) => priority.add(i));
      }
    }
    CORE_UNIVERSAL.forEach((i) => priority.add(i));
    const missed = HABITS.filter(
      (h) => h.action === "executed" && priority.has(h.id) && !todayLogged[h.id],
    );
    return missed.slice(0, 5);
  }, [conditions, todayLogged, bg]);

  // Rotate the carousel every 5s
  useEffect(() => {
    if (missedPrompts.length <= 1) return;
    const t = setInterval(() => setPromptIdx((i) => (i + 1) % missedPrompts.length), 5000);
    return () => clearInterval(t);
  }, [missedPrompts.length]);

  const activePrompt = missedPrompts.length > 0 ? missedPrompts[promptIdx % missedPrompts.length] : null;

  return (
    <div
      className={`p-4 rounded-2xl border ${
        isDark ? "border-fuchsia-500/20 bg-gradient-to-br from-fuchsia-500/5 to-cyan-500/5"
               : "border-fuchsia-200 bg-gradient-to-br from-fuchsia-50/60 to-cyan-50/60"
      }`}
      data-testid="quick-log-habits-card"
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-lg">🌱</span>
            <h3 className="font-bold text-sm">
              {bg ? "Живият метаболизъм — днешните ми навици" : "Living Metabolism — my habits today"}
            </h3>
          </div>
          <p className="text-[10px] opacity-70 mt-0.5">
            {bg
              ? "Отбележете какво сте направили или консумирали. Всеки клик тласка съответните KEGG пътища."
              : "Tap what you did or consumed. Each tap nudges the relevant KEGG pathways."}
          </p>
        </div>
      </div>

      {/* iter933 — Personalized "did-you-forget" carousel. Rotates every 5s
          through the top 5 missed high-priority habits mapped to the
          patient's accepted BDI/BPP conditions. One-click tap resolves
          the prompt (habit gets logged, next in queue rotates in). */}
      {activePrompt && (
        <button
          type="button"
          onClick={() => logHabit(activePrompt)}
          disabled={busy === activePrompt.id}
          className={`w-full mb-3 p-2.5 rounded-xl border flex items-center gap-2.5 transition-all text-left ${
            isDark ? "border-amber-400/30 bg-amber-500/10 hover:bg-amber-500/15 text-amber-100"
                   : "border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900"
          } disabled:opacity-60`}
          data-testid="quick-log-prompt-carousel"
        >
          <span className="text-lg shrink-0">🌱</span>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-bold">
              Забравили ли сте да отбележите {activePrompt.label_bg} днес?
            </div>
            <div className="text-[9px] opacity-70 mt-0.5">
              Персонализирано напомняне · {missedPrompts.length > 1 ? `${promptIdx + 1}/${missedPrompts.length}` : "1/1"} · натиснете за 1-клик отбелязване
            </div>
          </div>
          <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-white/50 dark:bg-white/10 shrink-0">
            ✓ Направих го
          </span>
        </button>
      )}

      {["food", "lifestyle", "attention"].map((grp) => {
        const meta = GROUP_META[grp];
        return (
          <div key={grp} className="mb-3 last:mb-0">
            <div className={`text-[10px] font-bold mb-1.5 opacity-70 uppercase tracking-wider`}>
              {meta.title_bg}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {groups[grp].map((h) => {
                const isBusy   = busy === h.id;
                const isDone   = flashed[h.id] || todayLogged[h.id];
                const isNeg    = h.action === "skipped";
                const baseCls  = "text-[11px] px-2.5 py-1.5 rounded-full font-medium flex items-center gap-1.5 transition-all border";
                const activeCls = isDone
                  ? (isNeg
                      ? (isDark ? "bg-rose-500/20 border-rose-400/40 text-rose-200"
                                : "bg-rose-100 border-rose-300 text-rose-800")
                      : (isDark ? "bg-emerald-500/25 border-emerald-400/50 text-emerald-100"
                                : "bg-emerald-100 border-emerald-300 text-emerald-800"))
                  : (isNeg
                      ? (isDark ? "bg-white/[0.04] border-rose-500/25 text-rose-200/80 hover:bg-rose-500/10"
                                : "bg-white border-rose-200 text-rose-700 hover:bg-rose-50")
                      : (isDark ? "bg-white/[0.04] border-white/10 text-white/80 hover:bg-white/[0.08]"
                                : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"));
                return (
                  <button
                    key={h.id}
                    type="button"
                    onClick={() => logHabit(h)}
                    disabled={isBusy || isDone}
                    className={`${baseCls} ${activeCls} disabled:cursor-default`}
                    data-testid={`habit-btn-${h.id}`}
                  >
                    {isBusy
                      ? <Loader2 className="w-3 h-3 animate-spin" />
                      : isDone
                        ? <Check className="w-3 h-3" />
                        : <span>{h.emoji}</span>}
                    <span>{h.label_bg}</span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}

      <div className="text-[9px] opacity-60 italic mt-2 border-t border-current/10 pt-2">
        {bg
          ? "Образователно проследяване, не медицинска препоръка. Няма нужда да пишете какво сте яли — само отбелязвайте."
          : "Educational tracker, not medical advice. No free-text needed — just tap."}
      </div>
    </div>
  );
}
